import React, { useEffect, useState } from 'react';
import { Grid, InputAdornment, TextField } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import UnitCollapsible from '@AMIEWEB/Unit/Common/UnitCollapsible';
import { makeStyles } from 'tss-react/mui';
import { getSearchFilterOptions } from 'app/services/SharedServices/SharedServices';
import {
  formatTypeAheadOptions,
  validDecimalAndLength,
} from '@AMIEWEB/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/ModificationsUtils';
import { orderBy } from 'lodash';
import { ITypeAheadOption } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PreferencesTab/CustomComponents/ControlledTypeAhead';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import AutoCompleteRenderChips from '@AMIEWEB/Order/OrderCreationForm/Common/AutoCompleteRenderChips';
import { useSelector } from 'react-redux';
import { selectOrderDetails } from '@AMIEWEB/Order/Store/OrderCreation.selectors';
import _ from 'lodash';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const SHIFT_FIELD_WIDTH = 2.55;
// Matches filled Shift autocomplete with a single chip (label + chip row), without capping growth.
const SHIFT_ROW_MIN_INPUT_HEIGHT = 60;

const useStyles = makeStyles()(theme => ({
  actionButtons: {
    color: `${theme.palette.system.neutralGray}`,
    borderRadius: '20px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${theme.palette.system.skyBlue}`,
      color: `${theme.palette.system.main}`,
    },
  },
  fieldStyle: {
    '& input': {
      paddingBlock: '26px 7px',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
    '& .MuiInputAdornment-positionEnd': {
      color: `${theme.palette.system.red}`,
    },
  },
  endAdornment: {
    alignItems: 'baseline',
    paddingRight: '128px',
    p: {
      fontSize: '14px',
    },
  },
  placementField: {
    '& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      display: 'none',
    },
  },
  shiftFieldWrapper: {
    '& .MuiFormControl-root': {
      margin: 0,
      width: '100%',
    },
    '& .MuiFilledInput-root': {
      minHeight: SHIFT_ROW_MIN_INPUT_HEIGHT,
      boxSizing: 'border-box',
      alignItems: 'flex-start',
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
  },
  shiftAutocompleteFieldStyle: {
    '& .MuiAutocomplete-inputRoot > .MuiBox-root': {
      padding: '6px 2px',
      overflowY: 'auto',
      maxHeight: '110px',
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: 'none',
    },
    '& .MuiChip-root': {
      maxWidth: 'none',
      height: 'auto',
    },
    '& .MuiChip-label': {
      overflow: 'visible',
      textOverflow: 'clip',
      whiteSpace: 'nowrap',
    },
    '& .MuiAutocomplete-input': {
      minWidth: '30px',
    },
  },
  timeFieldWrapper: {
    '& .MuiFormControl-root': {
      margin: 0,
      width: '100%',
    },
    '& .MuiFilledInput-root': {
      minHeight: SHIFT_ROW_MIN_INPUT_HEIGHT,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
    '& .MuiFilledInput-input': {
      padding: '26px 12px 7px',
    },
    '& .MuiInputAdornment-root': {
      alignSelf: 'center',
    },
  },
}));

export const ShiftsTile = () => {
  const [shiftOptions, setShiftOptions] = useState<ITypeAheadOption[]>([]);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);
  const order = useSelector(selectOrderDetails);
  const defaultShifts = order?.shifts || [];
  const { control, watch } = useFormContext();
  const { classes } = useStyles();
  const { t } = useTranslation();

  const watchValue = !!watch('shifts') ? watch('shifts') : [];

  const selectedShiftCount = useWatch({
    name: 'shifts',
    defaultValue: [],
  }).filter(field => !!field)?.length;

  const orderBillRate = useWatch({
    name: 'orderBillRate',
    defaultValue: '0',
  });
  const expectedHours = useWatch({
    name: 'expectedHours',
    defaultValue: '0',
  });
  const guaranteedHours = useWatch({
    name: 'guaranteedHours',
    defaultValue: '0',
  });
  const doubleDecimalRegex = /^(?:\d{1,2})(?:\.\d{0,2})?$/;

  const handleAccordionToggle = isExpanded => {
    setIsAccordionExpanded(isExpanded);
  };

  const getShiftDropDown = async () => {
    const options = await getSearchFilterOptions('shiftlist');

    if (options?.length > 0) {
      const newOptions = orderBy(formatTypeAheadOptions(options), 'id', 'asc');
      const transformedOptions: ITypeAheadOption[] = newOptions.map(x => {
        return {
          label: x.Description,
          object: {
            ID: x.id,
            description: x.Description,
          },
        };
      });
      setShiftOptions(transformedOptions);
    }
  };

  useEffect(() => {
    getShiftDropDown();
  }, []);

  return (
    <Grid item xs={12}>
      <UnitCollapsible
        title={t('order.newOrderCreation.tabs.shifts', {
          shiftCount: selectedShiftCount,
        })}
        updateIsExpanded={handleAccordionToggle}
        showIcon={false}
        children={
          <Grid container spacing={2} pt={'6px'} alignItems="flex-end">
            <Grid item xs={SHIFT_FIELD_WIDTH}>
              <Controller
                name="orderBillRate"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SingleLineInput
                    id="order-bill-rate"
                    inputName="orderBillRate"
                    inputType="number"
                    inputLabel={t('order.newOrder.orderBillRate')}
                    inputDefaultValue={parseInt(orderBillRate) ? parseFloat(orderBillRate).toFixed(2) : ''}
                    fieldStyle={`${classes.fieldStyle} ${classes.placementField}`}
                    inputValue={value}
                    handleChange={e => {
                      if (validDecimalAndLength(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                        onChange(e.target.value);
                      }
                    }}
                    handleBlur={() => onChange(isNaN(parseFloat(value)) ? undefined : parseFloat(value).toFixed(2))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={SHIFT_FIELD_WIDTH}>
              <Controller
                name={'expectedHours'}
                control={control}
                render={({ field }) => (
                  <SingleLineInput
                    id="expected-hours"
                    inputName="expectedHours"
                    inputLabel={t('order.newOrder.expectedHours')}
                    inputType="number"
                    fieldStyle={`${classes.fieldStyle} ${classes.placementField}`}
                    inputValue={field.value}
                    shrinkLabel={true}
                    inputDefaultValue={parseFloat(expectedHours) ? expectedHours : ''}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      let value = event.target.value;
                      const hours = parseFloat(value);
                      if (value.startsWith('.')) {
                        value = '0' + value;
                      }
                      if (isNaN(hours) || hours < 0) {
                        field.onChange('');
                      } else if (hours >= 0 && hours <= 99.99 && doubleDecimalRegex.test(value)) {
                        field.onChange(value);
                      }
                    }}
                    handleBlur={() => field.onChange(field.value === '' ? null : field.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment className={classes.endAdornment} position="end">
                          {'Hours'}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={SHIFT_FIELD_WIDTH}>
              <Controller
                name={'guaranteedHours'}
                control={control}
                render={({ field }) => (
                  <SingleLineInput
                    id="guaranteed-hours"
                    inputName="guaranteedHours"
                    inputLabel={t('order.orderIdHover.gww')}
                    inputType="number"
                    fieldStyle={`${classes.fieldStyle} ${classes.placementField}`}
                    inputValue={field.value}
                    inputDefaultValue={parseFloat(guaranteedHours) ? guaranteedHours : ''}
                    shrinkLabel={true}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      let value = event.target.value;
                      const hours = parseFloat(value);
                      if (value.startsWith('.')) {
                        value = '0' + value;
                      }
                      if (isNaN(hours) || hours < 0) {
                        field.onChange('');
                      } else if (hours >= 0 && hours <= 99.99 && doubleDecimalRegex.test(value)) {
                        field.onChange(value);
                      }
                    }}
                    handleBlur={() => field.onChange(field.value === '' ? null : field.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment className={classes.endAdornment} position="end">
                          {'Hours'}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={SHIFT_FIELD_WIDTH * 2} className={classes.shiftFieldWrapper}>
              <AutoCompleteRenderChips
                label={t('order.newOrderCreation.tabs.shiftDetails.shiftLabel')}
                options={shiftOptions}
                id="shifts-dropdown"
                name="shifts"
                disabledValue={[]}
                inputClassName={classes.shiftAutocompleteFieldStyle}
              />
            </Grid>
            <Grid item xs={SHIFT_FIELD_WIDTH} className={classes.timeFieldWrapper}>
              <Controller
                name="shiftStartTime"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      label="Shift Start Time"
                      value={value ?? null}
                      views={['hours', 'minutes']}
                      onChange={time => onChange(time)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="filled"
                          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'hh:mm am/pm',
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
            <Grid item xs={SHIFT_FIELD_WIDTH} className={classes.timeFieldWrapper}>
              <Controller
                name="shiftEndTime"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      label="Shift End Time"
                      value={value ?? null}
                      views={['hours', 'minutes']}
                      onChange={time => onChange(time)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="filled"
                          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'hh:mm am/pm',
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
          </Grid>
        }
      />
    </Grid>
  );
};
